
import get from 'lodash/get';
import catchify from 'catchify';
import Intersect from 'vue-intersect';
import { DEALS_URL } from '@/constants/api';
import CampaignsMixin from '~/mixins/campaigns';
import ProductCarousel from '../common/ProductCarousel';

const substitutePlaceholder = (url) => {
  const parts = url.split('/');
  if (parts.pop() === 'as-taco.svg') {
    parts.push('as-taco-16:9.png');
    return parts.join('/');
  }

  return url;
};

export default {
  components: {
    Intersect,
    ProductCarousel,
  },
  mixins: [CampaignsMixin],
  props: {
    collection: {
      type: Object,
      required: true,
    },
    useIntersection: {
      type: Boolean,
      default: false,
    },
    // jw: determines whether the smart collection should be displayed relative to its container or
    //     relative to the width of the viewport (currently only used for product carousels).
    relativeToContainer: {
      type: Boolean,
      default: false,
    },
  },
  async fetch() {
    if (!this.useIntersection) {
      await this.getDeals();
    }
  },
  data() {
    return {
      deals: [],
      isFetched: false,
      showCollection: true,
    };
  },
  computed: {
    startDate() {
      return this.collection.start_date;
    },
    profile() {
      if (this.collection && this.collection.deal_profile) {
        return this.collection.deal_profile.slug;
      }

      return false;
    },
    more() {
      if (this.collection.root_level) {
        return `/${this.collection.slug}/`;
      }
      return `/collections/${this.collection.slug}/`;
    },
    component() {
      return {
        background: this.collection.component_background,
        background_color: this.collection.component_background_color,
        mobile_background: this.collection.component_mobile_background,
        image: this.collection.component_image,
        promo: this.collection.component_promo,
        cta_link: this.collection.component_cta_link,
        cta_text: this.collection.component_cta_text,
      };
    },
    isLoggedIn() {
      return !!get(this, '$store.state.session.user.email');
    },
  },
  methods: {
    async getDeals() {
      if (this.isFetched) {
        return;
      }

      this.isFetched = !this.isFetched;
      const url = this.generateApiUrl();

      if (!url) return;
      const [error, response] = await catchify(this.$cachios.get(url));

      if (!error) {
        const { data: { deals = [] } = {} } = response;
        this.deals = deals;
        for (let i = 0; i < this.deals.length; i += 1) {
          const deal = this.deals[i];
          this.deals[i].featured_image_url = substitutePlaceholder(deal.featured_image_url);
          this.deals[i].media_url = substitutePlaceholder(deal.media_url);
        }
      }

      // In case no Deals, hide blank section
      if (this.deals.length === 0) {
        this.showCollection = false;
      }
    },
    generateApiUrl() {
      const col = this.collection;
      if (['countdown', 'email_capture', 'explore'].includes(col.component_type)) {
        return null;
      }
      const userId = (
        col.per_user_cache
        && this.$store.state.session
        && this.$store.state.session.user
        && this.$store.state.session.user.id
      ) || 'a';
      const dealIncludes = ['id', 'featured_image_url', 'public_name', 'is_free', 'get_absolute_url',
        'products.story.header', 'products.story.subheader', 'price', 'original_price',
        'deal_review', 'media_url', 'is_marketplace', 'unique_plan_types', 'dates', 'buy_button',
        'browse_deal_status', 'has_started', 'active_timer', 'products.story.card_description',
        'freebie_downloads_total', 'participation', 'products.internal_product_tags',
      ];
      const dealExcludes = ['*', 'products.*', 'products.story.*'];
      const urlParams = new URLSearchParams(col.query_parameters);
      const queryargs = [
        `per_page=${col.component_product_num || 12}`,
        `__cache=${userId}`,
      ];
      if (col.maximum_results) {
        queryargs.push(`maximum_results=${col.maximum_results}`);
      }
      urlParams.forEach((value, key) => {
        if ([
          'per_page',
          'status',
          'sort',
          'marketplace',
          'page',
          'query',
          'tags',
          'type',
        ].includes(key)) {
          queryargs.push(`${key}=${value}`);
        } else {
          queryargs.push(`profile{${key}}=${value}`);
        }
      });
      let url = `${DEALS_URL}/?${queryargs.join('&')}`;
      for (let i = 0; i < dealIncludes.length; i += 1) {
        const include = dealIncludes[i];
        url += `&include[]=${include}`;
      }
      for (let j = 0; j < dealExcludes.length; j += 1) {
        const exclude = dealExcludes[j];
        url += `&exclude[]=${exclude}`;
      }
      if (this.profile) {
        url += `&profile=${this.profile}`;
      }
      return url;
    },
  },
};
